export interface Theme {
  dark: ThemeColors
  light: ThemeColors
}

export interface ThemeColors {
  info: string
  error: string
  success: string
  warning: string
  primary: string
  surface: string
  secondary: string
  background: string
  [key: string]: string
}

export const DEFAULT_THEME = {
  name: 'blue-grey',
  dark: {
    info: '#64B5F6',
    error: '#E57373',
    success: '#81C784',
    warning: '#FFB74D',
    primary: '#4F5F68',
    surface: '#1E1E27',
    secondary: '#1E2123',
    background: '#181820',
  },
  light: {
    info: '#64B5F6',
    error: '#E57373',
    success: '#81C784',
    warning: '#FFB74D',
    primary: '#1D2C38',
    surface: '#FFFFFF',
    secondary: '#D2D5D7',
    background: '#F6F6F6',
  },
}

export const CATEGORY_THEMES = {
  'AI tooling': {
    name: 'orange',
    dark: {
      background: '#312D28',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#FFA726',
      secondary: '#413016',
      success: '#81C784',
      surface: '#38332B',
      warning: '#FFB74D',
    },
    light: {
      background: '#F4F1ED',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#FFA726',
      secondary: '#FEE8CC',
      success: '#81C784',
      surface: '#FFFEFD',
      warning: '#FFB74D',
    },
  },
  'Member tooling': {
    name: 'pink',
    dark: {
      background: '#302A2C',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#F06292',
      secondary: '#3F1F29',
      success: '#81C784',
      surface: '#372D30',
      warning: '#FFB74D',
    },
    light: {
      background: '#F4EEF0',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#EC407A',
      secondary: '#FBD9E4',
      success: '#81C784',
      surface: '#FFFDFE',
      warning: '#FFB74D',
    },
  },
  'Partner tooling': {
    name: 'teal',
    dark: {
      background: '#282D2D',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#26A69A',
      secondary: '#16302D',
      success: '#81C784',
      surface: '#2B3332',
      warning: '#FFB74D',
    },
    light: {
      background: '#EDF1F0',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#26A69A',
      secondary: '#CCE7E5',
      success: '#81C784',
      surface: '#FDFEFE',
      warning: '#FFB74D',
    },
  },
  'Data analytics': {
    name: 'purple',
    dark: {
      background: '#2D292E',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#AB47BC',
      secondary: '#311D34',
      success: '#81C784',
      surface: '#332D34',
      warning: '#FFB74D',
    },
    light: {
      background: '#F1EEF2',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#AB47BC',
      secondary: '#E8D3EE',
      success: '#81C784',
      surface: '#FEFDFE',
      warning: '#FFB74D',
    },
  },
  'Remote config': {
    name: 'blue',
    dark: {
      background: '#292D31',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#42A5F5',
      secondary: '#1C2F3F',
      success: '#81C784',
      surface: '#2D3338',
      warning: '#FFB74D',
    },
    light: {
      background: '#EEF1F4',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#42A5F5',
      secondary: '#D2E7FA',
      success: '#81C784',
      surface: '#FDFEFF',
      warning: '#FFB74D',
    },
  },
  'Release management': {
    name: 'indigo',
    dark: {
      background: '#2A2B2E',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#5C6BC0',
      secondary: '#212435',
      success: '#81C784',
      surface: '#2F3035',
      warning: '#FFB74D',
    },
    light: {
      background: '#EEEFF2',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#5C6BC0',
      secondary: '#D7DBEE',
      success: '#81C784',
      surface: '#FDFEFE',
      warning: '#FFB74D',
    },
  },
  'Content management': {
    name: 'green',
    dark: {
      background: '#2A2E2B',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#66BB6A',
      secondary: '#233424',
      success: '#81C784',
      surface: '#2F342F',
      warning: '#FFB74D',
    },
    light: {
      background: '#EFF1EF',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#66BB6A',
      secondary: '#D9ECDA',
      success: '#81C784',
      surface: '#FDFEFE',
      warning: '#FFB74D',
    },
  },
  'Quality monitoring': {
    name: 'red',
    dark: {
      background: '#302A2A',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#EF5350',
      secondary: '#3E1F1E',
      success: '#81C784',
      surface: '#372E2E',
      warning: '#FFB74D',
    },
    light: {
      background: '#F4EEEE',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#EF5350',
      secondary: '#FAD7D7',
      success: '#81C784',
      surface: '#FFFDFD',
      warning: '#FFB74D',
    },
  },
  'Design tooling': {
    name: 'yellow',
    dark: {
      background: '#31302A',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#FFEE58',
      secondary: '#413E20',
      success: '#81C784',
      surface: '#38372E',
      warning: '#FFB74D',
    },
    light: {
      background: '#F4F3EE',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#FFEE58',
      secondary: '#FFF7D7',
      success: '#81C784',
      surface: '#FFFFFD',
      warning: '#FFB74D',
    },
  },
  'Service settings': {
    name: 'brown',
    dark: {
      background: '#2C2B2A',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#8D6E63',
      secondary: '#2B2422',
      success: '#81C784',
      surface: '#32302F',
      warning: '#FFB74D',
    },
    light: {
      background: '#F0EFEF',
      error: '#E57373',
      info: '#64B5F6',
      primary: '#8D6E63',
      secondary: '#E2DBD9',
      success: '#81C784',
      surface: '#FEFEFD',
      warning: '#FFB74D',
    },
  },
}

export function getCategoryThemes(categoryName?: keyof typeof CATEGORY_THEMES): Theme {
  return categoryName ? (CATEGORY_THEMES[categoryName] ?? DEFAULT_THEME) : DEFAULT_THEME
}
